import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchOrgSummary, fetchOrgTrend, fetchOrgBranchTopItems, fetchOrgSlowItems, fetchOrgRatingsTrend, fetchOrgPatterns, fetchOrgLowRated, fetchOrgCompareItems, fetchOrgServiceSpeed, fetchOrgStationSpeed, fetchOrgAlerts, fetchOrgFrontDesk, fetchOrgWellness,
  getOrgInfo, getOrgToken, clearOrgSession, resolveImageUrl,
} from "../services/api";
import { formatPrice } from "../utils/currency";
import FrontDeskFacts from "../components/FrontDeskFacts";

// Owner dashboard i18n. AM/OM are machine drafts (2026-06) pending native review.
const STRINGS = {
  en: {
    sign_out: "Sign out", owner_dashboard: "Owner / General Manager Dashboard", branch: "branch", branches_word: "branches",
    multi_locked_title: "Multi-branch is an Enterprise feature",
    multi_locked_body: "Your group has {n} branches but your plan shows one at a time. Upgrade to Enterprise to see all branches together.",
    as_of: "As of", today: "Today", last7: "7 days", last30: "30 days", from: "From", to: "To",
    loading: "Loading…", could_not_load: "Could not load the summary.",
    kpi_branches: "Branches", kpi_orders: "Orders", kpi_revenue: "Revenue", kpi_aov: "Avg order", vs_prev: "vs prev.", kpi_avg_food: "Avg food rating",
    revenue_trend: "Revenue trend", all_branches_total: "All branches (total)",
    busy_title: "When you're busy", svc_title: "\u23f1\ufe0f Service speed", svc_cook: "Cook time", svc_wait: "Total wait", svc_min: "min", svc_avg: "avg", svc_orders: "orders", svc_none: "No timing data yet \u2014 it builds up as orders move through the kitchen.", by_daypart: "By daypart", by_dow: "By day of week", dp_breakfast: "Breakfast", dp_lunch: "Lunch", dp_dinner: "Dinner", dp_late: "Late", d1: "Mon", d2: "Tue", d3: "Wed", d4: "Thu", d5: "Fri", d6: "Sat", d7: "Sun",
    rating_trend: "Rating trend", weekly: "Weekly", monthly: "Monthly", food: "Food", service: "Service",
    no_ratings: "No ratings in this range yet.", no_data: "No data in this range yet.",
    revenue_by_branch: "Revenue by branch", orders_by_branch: "Orders by branch",
    branch_facts: "Branch facts", waiters: "Waiters", chefs: "Chefs", tables_qr: "Tables (QR)",
    th_food_cost: "Food cost %", th_purchasing: "Purchasing", th_shrinkage: "Shrinkage", th_cxl: "Cancel %", th_events: "Event rev.",
    rooms_title: "Rooms (front desk)", rooms_booked: "Rooms booked", rooms_occ: "Occupancy", rooms_nights: "Room-nights", rooms_revenue: "Room revenue", rooms_origins: "Where guests come from", rooms_unknown: "Unknown",
    well_title: "Wellness", well_revenue: "Service revenue", well_bookings: "Bookings", well_members: "Active members", well_dues: "Membership dues", well_by_branch: "By branch",
    top_items: "Top items (most ordered)", not_selling: "Not selling (slow movers)",
    top_btn: "Top", not_selling_btn: "Not selling", sold: "sold",
    no_items_sold: "No items sold in this range.", all_items_sales: "Every item has sales in this range.",
    branch_detail: "Branch detail", th_branch: "Branch", th_plan: "Plan",
    tab_overview: "Overview", tab_ratings: "Ratings", tab_branches: "Branch performance", ratings_by_branch: "Ratings by branch", th_item: "Item", th_sold: "Sold", th_rating: "Rating",
    cmp_view: "Compare branches", per_branch: "Per branch", cmp_title: "Same dish across branches", cmp_hint: "Best when branches share a menu — items not on a branch\u2019s menu show a blank.", cmp_shared: "in {n} branches", cmp_none: "No shared items sold in this period.", cmp_loading: "Loading comparison\u2026",
    me_title: "Menu engineering", me_sub: "Each dish by how often it sells and the profit it makes.", me_star: "Stars", me_star_h: "Popular & profitable — feature & protect", me_plow: "Plowhorses", me_plow_h: "Popular but low margin — lift the margin", me_puzzle: "Puzzles", me_puzzle_h: "Profitable but rarely ordered — promote", me_dog: "Dogs", me_dog_h: "Low sales & low margin — consider dropping", me_no_cost: "Add item costs in the menu editor to unlock profit analysis.", me_needs_cost: "No cost yet for: {n}",
    fvs_title: "Food vs service", fvs_food: "Food", fvs_service: "Service", fvs_gap: "{w} is the weaker area, by {n}★", fvs_even: "Food and service are even", lowrated_title: "Lowest-rated dishes", lowrated_sub: "Worst food ratings first — quality issues to check.", lowrated_none: "No low-rated dishes (need 2+ ratings).", nratings: "{n} ratings", coverage_label: "Rating coverage", coverage_text: "{r} of {t} orders rated ({p}%)", coverage_low: "small sample — read averages with care",
    no_branches: "No branches yet. Ask your ODRA admin to attach branches to your group.",
    alerts_title: "Needs attention", alerts_none: "Nothing needs attention right now.", alerts_window: "last 7 days",
    ops_title: "Operations", cancel_rate: "Cancellation rate", turnover_label: "Table turnover", turnover_hint: "orders per table",
    lead_title: "Branch leaderboard", contribution: "% of group", th_revenue: "Revenue",
  },
  am: {
    sign_out: "ውጣ", owner_dashboard: "የባለቤት / ዋና ሥራ አስኪያጅ ዳሽቦርድ", branch: "ቅርንጫፍ", branches_word: "ቅርንጫፎች",
    multi_locked_title: "ብዙ ቅርንጫፍ የ Enterprise አገልግሎት ነው",
    multi_locked_body: "ቡድንዎ {n} ቅርንጫፎች አሉት ነገር ግን እቅድዎ በአንድ ጊዜ አንዱን ያሳያል። ሁሉንም ቅርንጫፎች አንድ ላይ ለማየት ወደ Enterprise ያሳድጉ።",
    as_of: "እስከ", today: "ዛሬ", last7: "7 ቀናት", last30: "30 ቀናት", from: "ከ", to: "እስከ",
    loading: "በመጫን ላይ…", could_not_load: "ማጠቃለያውን መጫን አልተቻለም።",
    kpi_branches: "ቅርንጫፎች", kpi_orders: "ትዕዛዞች", kpi_revenue: "ገቢ", kpi_aov: "አማካይ ትዕዛዝ", vs_prev: "ካለፈው", kpi_avg_food: "አማካይ የምግብ ደረጃ",
    revenue_trend: "የገቢ አዝማሚያ", all_branches_total: "ሁሉም ቅርንጫፎች (ድምር)",
    busy_title: "መቼ እንደምትበዙ", svc_title: "\u23f1\ufe0f የአገልግሎት ፍጥነት", svc_cook: "የማብሰያ ጊዜ", svc_wait: "ጠቅላላ ቆይታ", svc_min: "ደቂቃ", svc_avg: "አማካይ", svc_orders: "ትዕዛዞች", svc_none: "እስካሁን የጊዜ መረጃ የለም \u2014 ትዕዛዞች በኩሽና ሲያልፉ ይከማቻል።", by_daypart: "በቀኑ ክፍል", by_dow: "በሳምንቱ ቀን", dp_breakfast: "ቁርስ", dp_lunch: "ምሳ", dp_dinner: "እራት", dp_late: "ሌሊት", d1: "ሰኞ", d2: "ማክሰ", d3: "ረቡዕ", d4: "ሐሙስ", d5: "ዓርብ", d6: "ቅዳሜ", d7: "እሁድ",
    rating_trend: "የደረጃ አዝማሚያ", weekly: "ሳምንታዊ", monthly: "ወርሃዊ", food: "ምግብ", service: "አገልግሎት",
    no_ratings: "በዚህ ክልል ውስጥ እስካሁን ደረጃ የለም።", no_data: "በዚህ ክልል ውስጥ እስካሁን ውሂብ የለም።",
    revenue_by_branch: "በቅርንጫፍ ገቢ", orders_by_branch: "በቅርንጫፍ ትዕዛዞች",
    branch_facts: "የቅርንጫፍ መረጃ", waiters: "አስተናጋጆች", chefs: "ሼፎች", tables_qr: "ጠረጴዛዎች (QR)",
    th_food_cost: "የምግብ ወጪ %", th_purchasing: "ግዢ", th_shrinkage: "ብክነት", th_cxl: "ስረዛ %", th_events: "የዝግጅት ገቢ",
    rooms_title: "ክፍሎች (የፊት ዴስክ)", rooms_booked: "የተያዙ ክፍሎች", rooms_occ: "ተሞልቶነት", rooms_nights: "የክፍል ሌሊቶች", rooms_revenue: "የክፍል ገቢ", rooms_origins: "እንግዶች ከየት ይመጣሉ", rooms_unknown: "ያልታወቀ",
    well_title: "Wellness", well_revenue: "Service revenue", well_bookings: "Bookings", well_members: "Active members", well_dues: "Membership dues", well_by_branch: "By branch", // TODO verify
    top_items: "ከፍተኛ ዕቃዎች (በብዛት የታዘዙ)", not_selling: "የማይሸጡ (ቀስ የሚንቀሳቀሱ)",
    top_btn: "ከፍተኛ", not_selling_btn: "የማይሸጥ", sold: "ተሸጧል",
    no_items_sold: "በዚህ ክልል ውስጥ የተሸጠ ዕቃ የለም።", all_items_sales: "ሁሉም ዕቃ በዚህ ክልል ሽያጭ አለው።",
    branch_detail: "የቅርንጫፍ ዝርዝር", th_branch: "ቅርንጫፍ", th_plan: "ዕቅድ",
    tab_overview: "አጠቃላይ እይታ", tab_ratings: "ደረጃዎች", tab_branches: "የቅርንጫፍ አፈጻጸም", ratings_by_branch: "በቅርንጫፍ ደረጃዎች", th_item: "ዕቃ", th_sold: "ተሸጧል", th_rating: "ደረጃ",
    cmp_view: "ቅርንጫፎችን አወዳድር", per_branch: "በቅርንጫፍ", cmp_title: "ተመሳሳይ ምግብ በቅርንጫፎች", cmp_hint: "ቅርንጫፎች ተመሳሳይ ምናሌ ሲኖራቸው የተሻለ ነው — በቅርንጫፉ ምናሌ ላይ የሌሉ ዕቃዎች ባዶ ይታያሉ።", cmp_shared: "በ{n} ቅርንጫፎች", cmp_none: "በዚህ ጊዜ ውስጥ የተሸጠ የጋራ ዕቃ የለም።", cmp_loading: "ንጽጽር በመጫን ላይ\u2026",
    me_title: "የምናሌ ትንተና", me_sub: "እያንዳንዱ ምግብ በሚሸጥበት ድግግሞሽ እና በሚያስገኘው ትርፍ።", me_star: "ኮከቦች", me_star_h: "ተወዳጅ እና ትርፋማ — አጉሉ ይጠብቁ", me_plow: "ጋማ ፈረሶች", me_plow_h: "ተወዳጅ ግን ዝቅተኛ ትርፍ — ትርፉን ከፍ ያድርጉ", me_puzzle: "እንቆቅልሾች", me_puzzle_h: "ትርፋማ ግን ብዙም የማይታዘዙ — ያስተዋውቁ", me_dog: "ውሾች", me_dog_h: "ዝቅተኛ ሽያጭ እና ትርፍ — ማስወገድ ያስቡ", me_no_cost: "የትርፍ ትንተና ለመክፈት በምናሌ አርታኢ ውስጥ የዕቃ ወጪዎችን ያስገቡ።", me_needs_cost: "እስካሁን ወጪ የሌላቸው: {n}",
    fvs_title: "ምግብ vs አገልግሎት", fvs_food: "ምግብ", fvs_service: "አገልግሎት", fvs_gap: "{w} ይበልጥ ደካማ ነው፣ በ{n}★", fvs_even: "ምግብ እና አገልግሎት እኩል ናቸው", lowrated_title: "ዝቅተኛ ደረጃ ያላቸው ምግቦች", lowrated_sub: "በዝቅተኛ የምግብ ደረጃ መጀመሪያ — የጥራት ችግሮች።", lowrated_none: "ዝቅተኛ ደረጃ ያላቸው ምግቦች የሉም (2+ ግምገማ ያስፈልጋል)።", nratings: "{n} ግምገማዎች", coverage_label: "የግምገማ ሽፋን", coverage_text: "ከ{t} ትዕዛዞች {r} ተገምግመዋል ({p}%)", coverage_low: "አነስተኛ ናሙና — አማካዮችን በጥንቃቄ ይመልከቱ",
    no_branches: "እስካሁን ቅርንጫፎች የሉም። ቅርንጫፎችን ወደ ቡድንዎ እንዲያያይዙ የODRA አስተዳዳሪዎን ይጠይቁ።",
    alerts_title: "ትኩረት ይፈልጋል", alerts_none: "አሁን ትኩረት የሚፈልግ ነገር የለም።", alerts_window: "ባለፉት 7 ቀናት",
    ops_title: "ኦፐሬሽን", cancel_rate: "የተሰረዙ ምጣኔ", turnover_label: "የጠረጴዛ ዙር", turnover_hint: "በጠረጴዛ ትዕዛዞች",
    lead_title: "የቅርንጫፍ ደረጃ", contribution: "% ከቡድኑ", th_revenue: "ገቢ",
  },
  om: {
    sign_out: "Ba'i", owner_dashboard: "Daashboordii Abbaa / Bulchaa Ol'aanaa", branch: "damee", branches_word: "dameewwan",
    multi_locked_title: "Damee hedduu tajaajila Enterprise ti",
    multi_locked_body: "Gareen kee damee {n} qaba garuu karoorri kee yeroo tokkotti tokko agarsiisa. Dameewwan hunda walitti agaruuf gara Enterprise guddisi.",
    as_of: "Hanga", today: "Har'a", last7: "Guyyaa 7", last30: "Guyyaa 30", from: "Irraa", to: "Hanga",
    loading: "Fe'aa jira…", could_not_load: "Cuunfaa fe'uu hin dandeenye.",
    kpi_branches: "Dameewwan", kpi_orders: "Ajajawwan", kpi_revenue: "Galii", kpi_aov: "Ajaja giddugaleessaa", vs_prev: "kan darbee caalaa", kpi_avg_food: "Giddugaleessa madaallii nyaataa",
    revenue_trend: "Adeemsa galii", all_branches_total: "Dameewwan hunda (ida'ama)",
    busy_title: "Yeroo baay'inni jiru", svc_title: "\u23f1\ufe0f Saffisa tajaajilaa", svc_cook: "Yeroo bilcheessuu", svc_wait: "Eegumsa guutuu", svc_min: "daq.", svc_avg: "gidd.", svc_orders: "ajajoota", svc_none: "Ammaaf daataan yeroo hin jiru \u2014 ajajni kushiinaa keessa yoo darbu walitti qabama.", by_daypart: "Yeroo guyyaatiin", by_dow: "Guyyaa torbaniitiin", dp_breakfast: "Ciree", dp_lunch: "Laaqana", dp_dinner: "Irbaata", dp_late: "Halkan", d1: "Wix", d2: "Kib", d3: "Rob", d4: "Kam", d5: "Jim", d6: "San", d7: "Dil",
    rating_trend: "Adeemsa madaallii", weekly: "Torbaniin", monthly: "Ji'aan", food: "Nyaata", service: "Tajaajila",
    no_ratings: "Hanga ammaatti madaallii hin jiru.", no_data: "Hanga ammaatti daataa hin jiru.",
    revenue_by_branch: "Galii damee tokkoon", orders_by_branch: "Ajaja damee tokkoon",
    branch_facts: "Ragaa damee", waiters: "Tajaajiltoota", chefs: "Kuksiiwwan", tables_qr: "Minjaalota (QR)",
    th_food_cost: "Baasii nyaataa %", th_purchasing: "Bittaa", th_shrinkage: "Hir'ina", th_cxl: "Haquu %", th_events: "Galii sagantaa",
    rooms_title: "Kutaalee (fuula duraa)", rooms_booked: "Kutaalee qabaman", rooms_occ: "Guutuu", rooms_nights: "Halkanoota kutaa", rooms_revenue: "Galii kutaa", rooms_origins: "Keessummoonni eessaa dhufu", rooms_unknown: "Hin beekamne",
    well_title: "Wellness", well_revenue: "Service revenue", well_bookings: "Bookings", well_members: "Active members", well_dues: "Membership dues", well_by_branch: "By branch", // TODO verify
    top_items: "Meeshaalee ol'aanoo (baay'inaan ajajaman)", not_selling: "Hin gurguramne",
    top_btn: "Ol'aanaa", not_selling_btn: "Hin gurguramne", sold: "gurgurame",
    no_items_sold: "Meeshaan gurgurame hin jiru.", all_items_sales: "Meeshaan hundi gurgurtaa qaba.",
    branch_detail: "Ibsa damee", th_branch: "Damee", th_plan: "Karoora",
    tab_overview: "Waliigala", tab_ratings: "Madaallii", tab_branches: "Raawwii damee", ratings_by_branch: "Madaallii damee tokkoon", th_item: "Meeshaa", th_sold: "Gurgurame", th_rating: "Madaallii",
    cmp_view: "Damee wal bira qabi", per_branch: "Damee tokkoon", cmp_title: "Nyaata wal fakkaatu damee gidduutti", cmp_hint: "Yeroo dameewwan menu wal fakkaatu qaban gaarii dha — wantoonni menu damee irra hin jirre duwwaa mul\u2019atu.", cmp_shared: "dameewwan {n} keessatti", cmp_none: "Yeroo kana keessatti meeshaan waliinii hin gurguramne.", cmp_loading: "Walbira qabuu fe\u2019aa jira\u2026",
    me_title: "Xiinxala menuu", me_sub: "Nyaata tokkoon tokkoon hangam akka gurguramuu fi bu'aa argamsiisuun.", me_star: "Urjiilee", me_star_h: "Jaalatamaa & bu'aa qabu — agarsiisi", me_plow: "Fardeen", me_plow_h: "Jaalatamaa garuu bu'aa xiqqaa — bu'aa guddisi", me_puzzle: "Hibboowwan", me_puzzle_h: "Bu'aa qabu garuu xiqqoo ajajamu — beeksisi", me_dog: "Saroota", me_dog_h: "Gurgurtaa fi bu'aa xiqqaa — haquu yaadi", me_no_cost: "Xiinxala bu'aa banuuf gulaalaa menuu keessatti baasii meeshaa galchi.", me_needs_cost: "Hanga ammaatti baasii hin qaban: {n}",
    fvs_title: "Nyaata vs tajaajila", fvs_food: "Nyaata", fvs_service: "Tajaajila", fvs_gap: "{w} laafaa dha, {n}★n", fvs_even: "Nyaata fi tajaajilli wal-qixa", lowrated_title: "Nyaata madaallii gad-aanaa", lowrated_sub: "Madaallii nyaataa gad-aanaa duraa — rakkoo qulqullinaa.", lowrated_none: "Nyaanni madaallii gad-aanaa hin jiru (madaallii 2+ barbaachisa).", nratings: "madaallii {n}", coverage_label: "Haguuggii madaallii", coverage_text: "ajaja {t} keessaa {r} madaalaman ({p}%)", coverage_low: "saampilii xiqqaa — giddugaleessa of eeggannoon ilaali",
    no_branches: "Hanga ammaatti dameewwan hin jiran. Dameewwan garee keessaniin akka walqabsiisan bulchaa ODRA gaafadhaa.",
    alerts_title: "Xiyyeeffannoo barbaada", alerts_none: "Amma wanti xiyyeeffannoo barbaadu hin jiru.", alerts_window: "guyyaa 7 darban",
    ops_title: "Hojii", cancel_rate: "Reeshoo haqamuu", turnover_label: "Naanna'uu minjaalaa", turnover_hint: "ajaja minjaala tokkoon",
    lead_title: "Sadarkaa damee", contribution: "% garee keessaa", th_revenue: "Galii",
  },
};
const LANGS = [["en", "EN"], ["am", "አማ"], ["om", "OM"]];

function fmt(d) {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
}
function daysAgo(n) { const x = new Date(); x.setDate(x.getDate() - n); return x; }

function Bars({ rows, valueKey, labelKey, fmtVal, color, t }) {
  const max = Math.max(1, ...rows.map((r) => r[valueKey] || 0));
  return (
    <div>
      {rows.length === 0 && <p className="muted">{t("no_data")}</p>}
      {rows.map((r, i) => (
        <div key={i} className="bar-row">
          <div className="bar-name" title={r[labelKey]}>{r[labelKey]}</div>
          <div className="bar-track">
            <div className="bar-fill" style={{ width: `${((r[valueKey] || 0) / max) * 100}%`, background: color }} />
          </div>
          <div className="bar-val">{fmtVal ? fmtVal(r) : r[valueKey]}</div>
        </div>
      ))}
    </div>
  );
}

function TrendChart({ series, cur, t }) {
  if (!series || series.length === 0) return <p className="muted">{t("no_data")}</p>;
  const W = 760, H = 230, padL = 56, padR = 14, padT = 14, padB = 30;
  const n = series.length;
  const max = Math.max(1, ...series.map((s) => s.revenue));
  const x = (i) => padL + (n <= 1 ? (W - padL - padR) / 2 : (i * (W - padL - padR)) / (n - 1));
  const y = (v) => padT + (H - padT - padB) * (1 - v / max);
  const linePts = series.map((s, i) => `${x(i).toFixed(1)},${y(s.revenue).toFixed(1)}`).join(" ");
  const areaPts = `${x(0).toFixed(1)},${y(0).toFixed(1)} ${linePts} ${x(n - 1).toFixed(1)},${y(0).toFixed(1)}`;
  const labelIdx = n <= 1 ? [0] : [0, Math.floor((n - 1) / 2), n - 1].filter((v, i, a) => a.indexOf(v) === i);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }} role="img" aria-label="Revenue trend">
      <line x1={padL} y1={y(0)} x2={W - padR} y2={y(0)} stroke="#e3e8ef" />
      <line x1={padL} y1={y(max)} x2={W - padR} y2={y(max)} stroke="#eef2f6" strokeDasharray="3 3" />
      <text x={padL - 8} y={y(max) + 4} textAnchor="end" fontSize="11" fill="#8a97a6">{formatPrice(max, cur)}</text>
      <text x={padL - 8} y={y(0) + 4} textAnchor="end" fontSize="11" fill="#8a97a6">0</text>
      <polygon points={areaPts} fill="#1D9E75" fillOpacity="0.12" />
      <polyline points={linePts} fill="none" stroke="#1D9E75" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      {series.map((s, i) => (
        <circle key={i} cx={x(i)} cy={y(s.revenue)} r="2.6" fill="#1D9E75">
          <title>{`${s.date}: ${formatPrice(s.revenue, cur)} · ${s.orders}`}</title>
        </circle>
      ))}
      {labelIdx.map((i) => (
        <text key={i} x={x(i)} y={H - 10} textAnchor="middle" fontSize="11" fill="#8a97a6">{series[i].date.slice(5)}</text>
      ))}
    </svg>
  );
}

function Kpi({ label, value, tint, accent, fg, delta, deltaLabel }) {
  const hasDelta = delta !== undefined && delta !== null;
  const up = hasDelta && delta >= 0;
  return (
    <div className="kpi-card" style={{ background: tint, borderLeft: `4px solid ${accent}`, borderRadius: 10, padding: "12px 16px" }}>
      <div style={{ fontSize: 13, color: "#5f6b78" }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: fg, marginTop: 2 }}>{value}</div>
      {hasDelta && (
        <div style={{ fontSize: 12, fontWeight: 600, marginTop: 2, color: up ? "#1d7a4d" : "#b3403a" }}>
          {up ? "▲" : "▼"} {Math.abs(delta)}%
          {deltaLabel ? <span style={{ color: "#8a93a0", fontWeight: 400 }}> {deltaLabel}</span> : null}
        </div>
      )}
    </div>
  );
}
function Mini({ label, value, tint, accent }) {
  return (
    <div style={{ background: tint || "#f3f6f9", borderLeft: `3px solid ${accent || "#cdd8e3"}`, borderRadius: 8, padding: "10px 12px" }}>
      <div style={{ fontSize: 12, color: "#5f6b78" }}>{label}</div>
      <div style={{ fontSize: 19, fontWeight: 600, marginTop: 1 }}>{value}</div>
    </div>
  );
}

function RatingTrendChart({ series, t }) {
  const pts = (series || []).filter((p) => p.avg_food != null);
  if (!series || pts.length === 0) return <p className="muted">{t("no_ratings")}</p>;
  const W = 760, H = 220, padL = 36, padR = 14, padT = 12, padB = 30;
  const n = series.length;
  const x = (i) => padL + (n <= 1 ? (W - padL - padR) / 2 : (i * (W - padL - padR)) / (n - 1));
  const y = (v) => padT + (H - padT - padB) * (1 - v / 5);
  const lineFor = (key) => series.map((s, i) => (s[key] != null ? `${x(i).toFixed(1)},${y(s[key]).toFixed(1)}` : null)).filter(Boolean).join(" ");
  const labelIdx = n <= 1 ? [0] : [0, Math.floor((n - 1) / 2), n - 1].filter((v, i, a) => a.indexOf(v) === i);
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }} role="img" aria-label="Average rating trend">
        {[0, 2.5, 5].map((g) => (
          <g key={g}>
            <line x1={padL} y1={y(g)} x2={W - padR} y2={y(g)} stroke="#eef2f6" />
            <text x={padL - 6} y={y(g) + 4} textAnchor="end" fontSize="11" fill="#8a97a6">{g}</text>
          </g>
        ))}
        <polyline points={lineFor("avg_food")} fill="none" stroke="#1D9E75" strokeWidth="2.5" strokeLinejoin="round" />
        <polyline points={lineFor("avg_service")} fill="none" stroke="#378ADD" strokeWidth="2.5" strokeLinejoin="round" strokeDasharray="5 4" />
        {series.map((s, i) => (s.avg_food != null ? (
          <circle key={i} cx={x(i)} cy={y(s.avg_food)} r="2.6" fill="#1D9E75"><title>{`${s.period}: ${s.avg_food} / ${s.avg_service} (${s.count})`}</title></circle>
        ) : null))}
        {labelIdx.map((i) => (
          <text key={i} x={x(i)} y={H - 10} textAnchor="middle" fontSize="11" fill="#8a97a6">{series[i].period.slice(5)}</text>
        ))}
      </svg>
      <div style={{ display: "flex", gap: 16, fontSize: 12, color: "#5f6b78", marginTop: 4 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 14, height: 3, background: "#1D9E75", display: "inline-block" }} />{t("food")}</span>
        <span style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 14, height: 3, background: "#378ADD", display: "inline-block" }} />{t("service")}</span>
      </div>
    </div>
  );
}

function ratingStyle(v) {
  if (v == null) return { bg: "transparent", fg: "#888" };
  if (v >= 4) return { bg: "#EAF3DE", fg: "#27500A" };
  if (v >= 3) return { bg: "#FAEEDA", fg: "#854F0B" };
  return { bg: "#FCEBEB", fg: "#A32D2D" };
}

export default function OwnerDashboardPage() {
  const nav = useNavigate();
  const [lang, setLangState] = useState(() => localStorage.getItem("odra_owner_lang") || "en");
  const t = (k) => (STRINGS[lang] && STRINGS[lang][k]) || STRINGS.en[k] || k;
  const setLang = (l) => { localStorage.setItem("odra_owner_lang", l); setLangState(l); };
  const [from, setFrom] = useState(fmt(daysAgo(6)));
  const [to, setTo] = useState(fmt(new Date()));
  const [fdOrg, setFdOrg] = useState(null);
  const [wellOrg, setWellOrg] = useState(null);
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState(null);
  const [trendBranch, setTrendBranch] = useState("all");
  const [factsBranch, setFactsBranch] = useState("");
  const [topItems, setTopItems] = useState(null);
  const [itemsMode, setItemsMode] = useState("top");
  const [ratingBucket, setRatingBucket] = useState("week");
  const [ratingTrend, setRatingTrend] = useState(null);
  const [patterns, setPatterns] = useState(null);
  const [orgSpeed, setOrgSpeed] = useState(null);
  const [stationSpeed, setStationSpeed] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [lowRated, setLowRated] = useState(null);
  const [patternsBranch, setPatternsBranch] = useState("all");
  const [compareItems, setCompareItems] = useState(null);
  const [compareOn, setCompareOn] = useState(!!(getOrgInfo() && getOrgInfo().merge_branch_items));
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const org = getOrgInfo();

  useEffect(() => { if (!getOrgToken()) nav("/owner/login", { replace: true }); }, [nav]);

  const loadSummary = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const sum = await fetchOrgSummary(from, to);
      setData(sum);
      setFactsBranch((cur) => cur || (sum.branches && sum.branches[0] ? String(sum.branches[0].id) : ""));
    } catch (e) {
      if (e && e.response && e.response.status === 401) { clearOrgSession(); nav("/owner/login", { replace: true }); return; }
      setErr(t("could_not_load"));
    } finally { setLoading(false); }
  }, [from, to, nav]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => {
    let alive = true;
    fetchOrgFrontDesk(from, to).then((r) => { if (alive) setFdOrg(r); }).catch(() => {});
    return () => { alive = false; };
  }, [from, to]);
  useEffect(() => {
    let alive = true;
    fetchOrgWellness({ from, to }).then((r) => { if (alive) setWellOrg(r); }).catch(() => {});
    return () => { alive = false; };
  }, [from, to]);

  useEffect(() => {
    let alive = true;
    fetchOrgTrend(from, to, trendBranch === "all" ? undefined : Number(trendBranch))
      .then((tr) => { if (alive) setTrend(tr); }).catch(() => {});
    return () => { alive = false; };
  }, [from, to, trendBranch]);

  useEffect(() => {
    let alive = true;
    fetchOrgPatterns(from, to, patternsBranch === "all" ? undefined : Number(patternsBranch))
      .then((p) => { if (alive) setPatterns(p); }).catch(() => {});
    return () => { alive = false; };
  }, [from, to, patternsBranch]);

  useEffect(() => {
    let alive = true;
    fetchOrgServiceSpeed(from, to).then((r) => { if (alive) setOrgSpeed(r); }).catch(() => {});
    return () => { alive = false; };
  }, [from, to]);

  useEffect(() => {
    let alive = true;
    fetchOrgStationSpeed(from, to).then((r) => { if (alive) setStationSpeed(r); }).catch(() => {});
    return () => { alive = false; };
  }, [from, to]);

  useEffect(() => {
    let alive = true;
    fetchOrgAlerts(7).then((r) => { if (alive) setAlerts(r); }).catch(() => {});
    return () => { alive = false; };
  }, [from, to]);

  useEffect(() => {
    if (!factsBranch) { setLowRated(null); return; }
    let alive = true; setLowRated(undefined);
    fetchOrgLowRated(Number(factsBranch), from, to)
      .then((r) => { if (alive) setLowRated(r); })
      .catch(() => { if (alive) setLowRated(null); });
    return () => { alive = false; };
  }, [factsBranch, from, to]);

  useEffect(() => {
    if (!factsBranch) { setTopItems(null); return; }
    let alive = true; setTopItems(undefined);
    const call = itemsMode === "slow"
      ? fetchOrgSlowItems(Number(factsBranch), from, to)
      : fetchOrgBranchTopItems(Number(factsBranch), from, to);
    call.then((r) => { if (alive) setTopItems(r); })
        .catch(() => { if (alive) setTopItems({ items: [] }); });
    return () => { alive = false; };
  }, [factsBranch, from, to, itemsMode]);

  useEffect(() => {
    let alive = true;
    fetchOrgRatingsTrend(ratingBucket).then((tr) => { if (alive) setRatingTrend(tr); }).catch(() => {});
    return () => { alive = false; };
  }, [ratingBucket]);

  useEffect(() => {
    if (tab !== "branches" || !compareOn) return;
    let alive = true; setCompareItems(undefined);
    fetchOrgCompareItems(from, to)
      .then((r) => { if (alive) setCompareItems(r); })
      .catch(() => { if (alive) setCompareItems({ branches: [], items: [] }); });
    return () => { alive = false; };
  }, [tab, compareOn, from, to]);

  const preset = (n) => { setFrom(fmt(daysAgo(n))); setTo(fmt(new Date())); };
  const logout = () => { clearOrgSession(); nav("/owner/login", { replace: true }); };

  const branches = (data && data.branches) || [];
  const cur = (branches[0] && branches[0].currency) || "ETB";
  const totals = (data && data.totals) || { branch_count: 0, orders: 0, revenue: 0 };
  const growth = (data && data.growth) || {};
  const dpLabel = { breakfast: t("dp_breakfast"), lunch: t("dp_lunch"), dinner: t("dp_dinner"), late: t("dp_late") };
  const daypartRows = ((patterns && patterns.daypart) || []).map((d) => ({ label: dpLabel[d.key] || d.key, revenue: d.revenue, orders: d.orders }));
  const dowRows = ((patterns && patterns.dow) || []).map((d) => ({ label: t("d" + d.day), revenue: d.revenue, orders: d.orders }));
  const rated = branches.filter((b) => b.avg_food_rating != null);
  const avgFood = rated.length ? (rated.reduce((s, b) => s + b.avg_food_rating, 0) / rated.length).toFixed(1) : "—";
  const factsB = branches.find((b) => String(b.id) === String(factsBranch));
  const hotelLogo = (org && org.logo_url) || (branches.length === 1 && branches[0] ? branches[0].logo_url : null);
  const groupCover = (data && data.organization && data.organization.cover_image_url) || null;
  const gFood = totals.avg_food, gService = totals.avg_service;
  const coverImg = groupCover || (branches.find((b) => b.cover_image_url) || {}).cover_image_url;
  const branchWord = branches.length === 1 ? t("branch") : t("branches_word");
  const multiLocked = !!(data && data.multi_branch_locked);
  const totalBranches = (totals && totals.total_branches) || branches.length;

  return (
    <div className="owner-wrap" style={{ minHeight: "100vh", background: "#f6f8fb" }}>
      <style>{`
        .owner-wrap .card{ box-shadow:0 1px 3px rgba(16,40,60,.06); border:1px solid #eef2f6; border-radius:14px; }
        .owner-wrap h3{ font-weight:600; color:#063b48; border-left:3px solid #008080; padding-left:9px; }
        .owner-wrap .kpi-card{ transition:transform .12s ease, box-shadow .12s ease; }
        .owner-wrap .kpi-card:hover{ transform:translateY(-2px); box-shadow:0 6px 16px rgba(16,40,60,.10); }
        .owner-wrap .lang-pill{ font-size:12px; padding:3px 9px; border:1px solid #cdd8e3; background:#fff; border-radius:7px; cursor:pointer; color:#5f6b78; }
        .owner-wrap .lang-pill.active{ background:#008080; color:#fff; border-color:#008080; }
        .owner-wrap .otab-bar{ background:#063b48; }
        .owner-wrap .otab-bar-inner{ max-width:1100px; margin:0 auto; padding:8px 16px; display:flex; gap:6px; flex-wrap:wrap; }
        .owner-wrap .otab{ padding:9px 18px; border:none; background:transparent; color:#cfe0e4; border-radius:9px; cursor:pointer; font-size:14px; }
        .owner-wrap .otab:hover{ background:rgba(255,255,255,.10); color:#fff; }
        .owner-wrap .otab.active{ background:#fff; color:#063b48; font-weight:600; }
      `}</style>
      <div style={{ height: 4, background: "#008080" }} />
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 20px", background: "#fff", borderBottom: "1px solid #eef2f6" }}>
        <img src="/logo-horizontal.png?v=1" alt="ODRA" style={{ height: 22, opacity: 0.92 }} />
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ display: "flex", gap: 4 }}>
            {LANGS.map(([code, label]) => (
              <button key={code} className={"lang-pill" + (lang === code ? " active" : "")} onClick={() => setLang(code)}>{label}</button>
            ))}
          </div>
          <button className="btn secondary small" onClick={logout}>{t("sign_out")}</button>
        </div>
      </header>

      <div style={{
        background: coverImg
          ? `linear-gradient(rgba(6,40,60,.55), rgba(6,40,60,.80)), url(${resolveImageUrl(coverImg)}) center/cover no-repeat`
          : "linear-gradient(120deg, #063b48 0%, #0a6b5f 100%)",
        borderBottom: "3px solid #2bb6a3",
      }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "26px 16px", display: "flex", alignItems: "center", gap: 16 }}>
          {hotelLogo ? (
            <div style={{ width: 60, height: 60, borderRadius: 14, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flex: "0 0 auto" }}>
              <img src={resolveImageUrl(hotelLogo)} alt="" style={{ maxWidth: "84%", maxHeight: "84%", objectFit: "contain" }} />
            </div>
          ) : (
            <div style={{ width: 60, height: 60, borderRadius: 14, background: "#fff", color: "#063b48", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 24, flex: "0 0 auto" }}>
              {((org && org.name) || "O").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: 24, color: "#fff" }}>{(org && org.name) || "Group"}</h2>
            <div style={{ color: "#cfe0e4", fontSize: 13 }}>{t("owner_dashboard")}{branches.length ? ` · ${branches.length} ${branchWord}` : ""}</div>
          </div>
          <div style={{ color: "#cfe0e4", fontSize: 12, textAlign: "right", whiteSpace: "nowrap" }}>
            {t("as_of")}<br /><b style={{ color: "#fff" }}>{new Date().toLocaleDateString()}</b>
          </div>
        </div>
      </div>

      <div className="otab-bar">
        <div className="otab-bar-inner">
          {[["overview", t("tab_overview")], ["ratings", t("tab_ratings")], ["branches", t("tab_branches")]].map(([k, lbl]) => (
            <button key={k} className={"otab" + (tab === k ? " active" : "")} onClick={() => setTab(k)}>{lbl}</button>
          ))}
        </div>
      </div>

      <div className="page" style={{ maxWidth: 1100, margin: "0 auto", padding: "20px 16px" }}>

        <div className="card" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button className="btn secondary small" onClick={() => preset(0)}>{t("today")}</button>
          <button className="btn secondary small" onClick={() => preset(6)}>{t("last7")}</button>
          <button className="btn secondary small" onClick={() => preset(29)}>{t("last30")}</button>
          <span style={{ flex: 1 }} />
          <label className="muted" style={{ fontSize: 13 }}>{t("from")} <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
          <label className="muted" style={{ fontSize: 13 }}>{t("to")} <input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
        </div>

        {multiLocked && (
          <div className="card" style={{ borderLeft: "4px solid #BA7517", background: "#FFF8EC", display: "flex", gap: 12, alignItems: "flex-start" }}>
            <span style={{ fontSize: 20, lineHeight: 1 }}>🔒</span>
            <div>
              <div style={{ fontWeight: 600, color: "#633806" }}>{t("multi_locked_title")}</div>
              <div className="muted" style={{ fontSize: 13.5, marginTop: 2 }}>
                {t("multi_locked_body").replace("{n}", String(totalBranches))}
              </div>
            </div>
          </div>
        )}
        {err && <div className="error">{err}</div>}
        {loading ? <p>{t("loading")}</p> : data ? (
          <>
            {tab === "overview" && (
            <>
            {alerts && alerts.alerts && alerts.alerts.length > 0 && (
              <section className="card" style={{ borderLeft: "4px solid #BA7517" }}>
                <h3 style={{ marginTop: 0 }}>{"\u26a0\ufe0f"} {t("alerts_title")} <span className="muted" style={{ fontSize: 12, fontWeight: 400 }}>{"\u00b7"} {t("alerts_window")}</span></h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {alerts.alerts.map((a, i) => {
                    const sev = a.severity === "high"
                      ? { bg: "#FCEBEB", bd: "#A32D2D", fg: "#7a1f1f" }
                      : a.severity === "medium"
                        ? { bg: "#FAEEDA", bd: "#BA7517", fg: "#633806" }
                        : { bg: "#EEF2F6", bd: "#94a3b8", fg: "#475569" };
                    return (
                      <div key={i} style={{ background: sev.bg, borderLeft: `3px solid ${sev.bd}`, borderRadius: 8, padding: "8px 12px" }}>
                        <span style={{ color: sev.fg, fontSize: 14 }}><b>{a.branch}</b> {"\u2014"} {a.message}</span>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px,1fr))", gap: 12, marginBottom: 4 }}>
              <Kpi label={t("kpi_branches")} value={totals.branch_count} tint="#E6F1FB" accent="#378ADD" fg="#0C447C" />
              <Kpi label={t("kpi_orders")} value={totals.orders.toLocaleString()} tint="#E1F5EE" accent="#1D9E75" fg="#085041" delta={growth.orders_pct} deltaLabel={t("vs_prev")} />
              <Kpi label={t("kpi_revenue")} value={formatPrice(totals.revenue, cur)} tint="#EAF3DE" accent="#639922" fg="#27500A" delta={growth.revenue_pct} deltaLabel={t("vs_prev")} />
              <Kpi label={t("kpi_aov")} value={formatPrice(totals.aov || 0, cur)} tint="#EEEDFE" accent="#7F77DD" fg="#2E2A6B" delta={growth.aov_pct} deltaLabel={t("vs_prev")} />
              <Kpi label={t("kpi_avg_food")} value={avgFood === "—" ? "—" : `${avgFood} ★`} tint="#FAEEDA" accent="#BA7517" fg="#633806" />
            </div>

            {totals.rooms_active > 0 && (
              <section className="card" style={{ marginBottom: 4 }}>
                <h3 style={{ margin: "0 0 10px" }}>{t("rooms_title")}</h3>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px,1fr))", gap: 12 }}>
                  <Kpi label={t("rooms_booked")} value={(totals.rooms_booked || 0).toLocaleString()} tint="#E6F1FB" accent="#378ADD" fg="#0C447C" />
                  <Kpi label={t("rooms_occ")} value={totals.room_occupancy_pct == null ? "—" : `${totals.room_occupancy_pct}%`} tint="#E1F5EE" accent="#1D9E75" fg="#085041" />
                  <Kpi label={t("rooms_nights")} value={(totals.room_nights || 0).toLocaleString()} tint="#FAEEDA" accent="#BA7517" fg="#633806" />
                  <Kpi label={t("rooms_revenue")} value={formatPrice(totals.room_revenue || 0, cur)} tint="#EAF3DE" accent="#639922" fg="#27500A" />
                </div>
                {(data.guest_origins || []).length > 0 && (() => {
                  const gmax = Math.max(1, ...(data.guest_origins || []).map((x) => x.count));
                  return (
                    <div style={{ marginTop: 14 }}>
                      <div style={{ fontWeight: 600, marginBottom: 8 }}>{t("rooms_origins")}</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {(data.guest_origins || []).slice(0, 8).map((o, i) => (
                          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ width: 150, fontSize: 13, color: "#3a4652", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.label === "Unknown" ? t("rooms_unknown") : o.label}</div>
                            <div style={{ flex: 1, background: "#eef2f6", borderRadius: 6, height: 16, overflow: "hidden" }}>
                              <div style={{ width: `${(o.count / gmax) * 100}%`, background: "#0F6E56", height: "100%" }} />
                            </div>
                            <div style={{ width: 30, textAlign: "right", fontSize: 13, fontWeight: 600 }}>{o.count}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </section>
            )}

            {fdOrg && fdOrg.totals && fdOrg.totals.rooms_total > 0 && (
              <section className="card" style={{ marginBottom: 4 }}>
                <FrontDeskFacts
                  snapshot={{
                    rooms: { total: fdOrg.totals.rooms_total, in_house: fdOrg.totals.in_house, arrivals_due: fdOrg.totals.arrivals_due, departures_due: fdOrg.totals.departures_due, occupancy_today_pct: fdOrg.totals.occupancy_today_pct },
                    period: { room_revenue: fdOrg.totals.room_revenue, adr: fdOrg.totals.adr, revpar: fdOrg.totals.revpar, occupancy_pct: fdOrg.totals.occupancy_pct },
                    owed: { in_house_balance: fdOrg.totals.in_house_balance, deposits_held: fdOrg.totals.deposits_held, company_owed: fdOrg.totals.company_owed },
                    events: { upcoming: fdOrg.totals.events_upcoming, revenue: fdOrg.totals.events_revenue, balance: fdOrg.totals.events_balance },
                  }}
                  cur={fdOrg.currency || cur}
                  scope="org"
                />
                {(fdOrg.branches || []).length > 1 && (
                  <div style={{ marginTop: 12, overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      <thead><tr>
                        <th style={{ textAlign: "left", color: "#5b6472", padding: "4px 6px" }}>{t("kpi_branches")}</th>
                        <th style={{ textAlign: "right", color: "#5b6472", padding: "4px 6px" }}>{t("rooms_occ")}</th>
                        <th style={{ textAlign: "right", color: "#5b6472", padding: "4px 6px" }}>{t("rooms_revenue")}</th>
                      </tr></thead>
                      <tbody>
                        {fdOrg.branches.map((b) => (
                          <tr key={b.branch_id}>
                            <td style={{ padding: "3px 6px" }}>{b.name}</td>
                            <td style={{ textAlign: "right", padding: "3px 6px" }}>{b.period.occupancy_pct == null ? "—" : `${b.period.occupancy_pct}%`}</td>
                            <td style={{ textAlign: "right", padding: "3px 6px" }}>{formatPrice(b.period.room_revenue || 0, fdOrg.currency || cur)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}

            {wellOrg && (wellOrg.branches || []).length > 0 && (
              <section className="card" style={{ marginBottom: 4 }}>
                <h3 style={{ margin: "0 0 10px" }}>{t("well_title")}</h3>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px,1fr))", gap: 12 }}>
                  <Kpi label={t("well_revenue")} value={formatPrice((wellOrg.totals && wellOrg.totals.services_revenue) || 0, cur)} tint="#EAF3DE" accent="#639922" fg="#27500A" />
                  <Kpi label={t("well_bookings")} value={((wellOrg.totals && wellOrg.totals.bookings_total) || 0).toLocaleString()} tint="#E6F1FB" accent="#378ADD" fg="#0C447C" />
                  <Kpi label={t("well_members")} value={((wellOrg.totals && wellOrg.totals.active_members) || 0).toLocaleString()} tint="#EEEDFE" accent="#7F77DD" fg="#2E2A6B" />
                  <Kpi label={t("well_dues")} value={formatPrice((wellOrg.totals && wellOrg.totals.membership_dues) || 0, cur)} tint="#FAEEDA" accent="#BA7517" fg="#633806" />
                </div>
                {(wellOrg.branches || []).length > 1 && (
                  <div style={{ marginTop: 12, overflowX: "auto" }}>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>{t("well_by_branch")}</div>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      <thead><tr>
                        <th style={{ textAlign: "left", color: "#5b6472", padding: "4px 6px" }}>{t("kpi_branches")}</th>
                        <th style={{ textAlign: "right", color: "#5b6472", padding: "4px 6px" }}>{t("well_revenue")}</th>
                        <th style={{ textAlign: "right", color: "#5b6472", padding: "4px 6px" }}>{t("well_bookings")}</th>
                        <th style={{ textAlign: "right", color: "#5b6472", padding: "4px 6px" }}>{t("well_members")}</th>
                      </tr></thead>
                      <tbody>
                        {wellOrg.branches.map((b) => (
                          <tr key={b.branch_id}>
                            <td style={{ padding: "3px 6px" }}>{b.name}</td>
                            <td style={{ textAlign: "right", padding: "3px 6px" }}>{formatPrice(b.services_revenue || 0, cur)}</td>
                            <td style={{ textAlign: "right", padding: "3px 6px" }}>{(b.bookings_total || 0).toLocaleString()}</td>
                            <td style={{ textAlign: "right", padding: "3px 6px" }}>{(b.active_members || 0).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}

            <section className="card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                <h3 style={{ margin: 0 }}>{t("busy_title")}</h3>
                <select value={patternsBranch} onChange={(e) => setPatternsBranch(e.target.value)}>
                  <option value="all">{t("all_branches_total")}</option>
                  {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px,1fr))", gap: 18 }}>
                <div>
                  <div className="muted" style={{ fontSize: 13, marginBottom: 6 }}>{t("by_daypart")}</div>
                  <Bars rows={daypartRows} valueKey="revenue" labelKey="label" fmtVal={(r) => formatPrice(r.revenue, cur)} color="#1D9E75" t={t} />
                </div>
                <div>
                  <div className="muted" style={{ fontSize: 13, marginBottom: 6 }}>{t("by_dow")}</div>
                  <Bars rows={dowRows} valueKey="revenue" labelKey="label" fmtVal={(r) => formatPrice(r.revenue, cur)} color="#7F77DD" t={t} />
                </div>
              </div>
            </section>

            {stationSpeed && Object.keys(stationSpeed.stations || {}).some((k) => k !== "kitchen") && (
            <section className="card">
              <h3 style={{ marginTop: 0 }}>🍳 Prep time by station</h3>
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                {["kitchen", "bar", "cafe"].filter((k) => stationSpeed.stations[k]).map((k) => {
                  const b = stationSpeed.stations[k];
                  const label = { kitchen: "🍳 Kitchen", bar: "🍺 Bar", cafe: "☕ Café" }[k];
                  return (
                    <div key={k} style={{ flex: "1 1 120px", background: "#F1F5F9", borderRadius: 12, padding: "12px 14px" }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>{label}</div>
                      <div style={{ fontSize: 24, fontWeight: 700, color: "#085041", lineHeight: 1.15 }}>
                        {b.median_min == null ? "\u2014" : <>{b.median_min} <span style={{ fontSize: 13 }}>{t("svc_min")}</span></>}
                      </div>
                      <div className="muted" style={{ fontSize: 12 }}>{b.n} items</div>
                    </div>
                  );
                })}
              </div>
            </section>
            )}
            {orgSpeed && (
            <section className="card">
              <h3 style={{ marginTop: 0 }}>{t("svc_title")}</h3>
              {(orgSpeed.totals.cook.n === 0 && orgSpeed.totals.total_wait.n === 0) ? (
                <p className="muted">{t("svc_none")}</p>
              ) : (
                <>
                  <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: branches.length > 1 ? 14 : 0 }}>
                    {[["cook", t("svc_cook"), "#FAEEDA", "#BA7517", "#633806"], ["total_wait", t("svc_wait"), "#E1F5EE", "#1D9E75", "#085041"]].map(([k, label, tint, accent, fg]) => {
                      const b = orgSpeed.totals[k];
                      return (
                        <div key={k} style={{ flex: "1 1 150px", background: tint, borderRadius: 12, padding: "12px 14px" }}>
                          <div style={{ fontSize: 13, color: fg, fontWeight: 600 }}>{label}</div>
                          <div style={{ fontSize: 26, fontWeight: 700, color: accent, lineHeight: 1.15 }}>
                            {b.median_min == null ? "\u2014" : <>{b.median_min} <span style={{ fontSize: 14, fontWeight: 600 }}>{t("svc_min")}</span></>}
                          </div>
                          <div className="muted" style={{ fontSize: 12 }}>
                            {b.median_min == null ? "\u2014" : `${t("svc_avg")} ${b.avg_min} \u00b7 ${b.n} ${t("svc_orders")}`}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {branches.length > 1 && (
                    <div style={{ overflowX: "auto" }}>
                      <table className="data" style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ background: "#008080" }}>
                            <th style={{ color: "#fff", textAlign: "left", padding: "8px 10px" }}>{t("th_branch")}</th>
                            <th style={{ color: "#fff", textAlign: "right", padding: "8px 10px" }}>{t("svc_cook")}</th>
                            <th style={{ color: "#fff", textAlign: "right", padding: "8px 10px" }}>{t("svc_wait")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orgSpeed.branches.map((b, i) => (
                            <tr key={b.id} style={{ background: i % 2 ? "#f2f9f8" : "#fff" }}>
                              <td style={{ padding: "6px 10px" }}><b>{b.name}</b></td>
                              <td style={{ padding: "6px 10px", textAlign: "right" }}>{b.cook.median_min == null ? "\u2014" : `${b.cook.median_min} ${t("svc_min")}`}</td>
                              <td style={{ padding: "6px 10px", textAlign: "right" }}>{b.total_wait.median_min == null ? "\u2014" : `${b.total_wait.median_min} ${t("svc_min")}`}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </section>
            )}

            <section className="card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <h3 style={{ margin: 0 }}>{t("revenue_trend")}</h3>
                <select value={trendBranch} onChange={(e) => setTrendBranch(e.target.value)}>
                  <option value="all">{t("all_branches_total")}</option>
                  {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <TrendChart series={trend && trend.series} cur={cur} t={t} />
            </section>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }} className="owner-charts">
              <section className="card">
                <h3 style={{ marginTop: 0 }}>{t("revenue_by_branch")}</h3>
                <Bars rows={branches} valueKey="revenue" labelKey="name" color="#1D9E75" t={t} fmtVal={(r) => formatPrice(r.revenue, r.currency || cur)} />
              </section>
              <section className="card">
                <h3 style={{ marginTop: 0 }}>{t("orders_by_branch")}</h3>
                <Bars rows={branches} valueKey="orders" labelKey="name" color="#378ADD" t={t} />
              </section>
            </div>
            </>
            )}

            {tab === "ratings" && (
            <>
            {totals.orders > 0 && (
              <div className="card" style={{ padding: "10px 14px", borderLeft: `4px solid ${(totals.coverage_pct || 0) < 20 ? "#BA7517" : "#1D9E75"}`, background: (totals.coverage_pct || 0) < 20 ? "#FAEEDA" : "#E1F5EE" }}>
                <b style={{ color: (totals.coverage_pct || 0) < 20 ? "#633806" : "#085041" }}>{t("coverage_label")}: </b>
                <span style={{ color: (totals.coverage_pct || 0) < 20 ? "#633806" : "#085041" }}>{t("coverage_text").replace("{r}", totals.rated_orders || 0).replace("{t}", totals.orders).replace("{p}", totals.coverage_pct || 0)}</span>
                {(totals.rated_orders || 0) < 5 && <span className="muted"> — {t("coverage_low")}</span>}
              </div>
            )}
            <section className="card">
              <h3 style={{ marginTop: 0 }}>{t("fvs_title")}</h3>
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "center" }}>
                <div style={{ textAlign: "center" }}>
                  <div className="muted" style={{ fontSize: 13 }}>{t("fvs_food")}</div>
                  <div style={{ fontSize: 30, fontWeight: 700, color: "#BA7517" }}>{gFood == null ? "—" : `${gFood} ★`}</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div className="muted" style={{ fontSize: 13 }}>{t("fvs_service")}</div>
                  <div style={{ fontSize: 30, fontWeight: 700, color: "#378ADD" }}>{gService == null ? "—" : `${gService} ★`}</div>
                </div>
                <div style={{ flex: 1, minWidth: 180 }}>
                  {gFood != null && gService != null && (
                    Math.abs(gFood - gService) < 0.05
                      ? <span className="muted">{t("fvs_even")}</span>
                      : <span style={{ fontWeight: 600, color: "#633806" }}>{t("fvs_gap").replace("{w}", gService < gFood ? t("fvs_service") : t("fvs_food")).replace("{n}", Math.abs(gFood - gService).toFixed(1))}</span>
                  )}
                </div>
              </div>
            </section>

            <section className="card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <h3 style={{ margin: 0 }}>{t("rating_trend")}</h3>
                <div style={{ display: "flex", gap: 6 }}>
                  <button className={"btn small " + (ratingBucket === "week" ? "" : "secondary")} onClick={() => setRatingBucket("week")}>{t("weekly")}</button>
                  <button className={"btn small " + (ratingBucket === "month" ? "" : "secondary")} onClick={() => setRatingBucket("month")}>{t("monthly")}</button>
                </div>
              </div>
              <RatingTrendChart series={ratingTrend && ratingTrend.series} t={t} />
            </section>

            <section className="card">
              <h3 style={{ marginTop: 0 }}>{t("ratings_by_branch")}</h3>
              <table className="data">
                <thead><tr><th>{t("th_branch")}</th><th style={{ textAlign: "right" }}>{t("food")}</th><th style={{ textAlign: "right" }}>{t("service")}</th></tr></thead>
                <tbody>
                  {branches.map((b) => (
                    <tr key={b.id}>
                      <td><b>{b.name}</b></td>
                      <td style={{ textAlign: "right" }}>{b.avg_food_rating == null ? "—" : `${b.avg_food_rating} ★`}</td>
                      <td style={{ textAlign: "right" }}>{b.avg_service_rating == null ? "—" : `${b.avg_service_rating} ★`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            <section className="card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <h3 style={{ margin: 0 }}>{t("lowrated_title")}</h3>
                <select value={factsBranch} onChange={(e) => setFactsBranch(e.target.value)}>
                  {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <p className="muted" style={{ marginTop: -2 }}>{t("lowrated_sub")}</p>
              {lowRated === undefined ? <p className="muted">{t("loading")}</p> :
               !lowRated || !lowRated.items || lowRated.items.length === 0 ? <p className="muted">{t("lowrated_none")}</p> : (
                <div>
                  {lowRated.items.map((it, i) => {
                    const rb = ratingStyle(it.avg_food_rating);
                    return (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderTop: i ? "1px solid #eef2f6" : "none" }}>
                        {it.image_url ? <img src={resolveImageUrl(it.image_url)} alt="" style={{ width: 38, height: 38, borderRadius: 8, objectFit: "cover" }} /> : <div style={{ width: 38, height: 38, borderRadius: 8, background: "#eef2f6" }} />}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <b>{it.name}</b>
                          <div className="muted" style={{ fontSize: 12 }}>{t("nratings").replace("{n}", it.n_ratings)}</div>
                        </div>
                        <span style={{ background: rb.bg, color: rb.fg, padding: "2px 8px", borderRadius: 999, fontSize: 12, fontWeight: 600 }}>{it.avg_food_rating} ★</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
            </>
            )}

            {tab === "branches" && (
            <>
            <section className="card">
              <h3 style={{ marginTop: 0 }}>{"\ud83e\uddfe"} {t("ops_title")}</h3>
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: branches.length > 1 ? 12 : 8 }}>
                <div style={{ flex: "1 1 150px", background: "#FCEBEB", borderRadius: 12, padding: "12px 14px" }}>
                  <div style={{ fontSize: 13, color: "#7a1f1f", fontWeight: 600 }}>{t("cancel_rate")}</div>
                  <div style={{ fontSize: 26, fontWeight: 700, color: "#A32D2D", lineHeight: 1.15 }}>{totals.cancellation_rate != null ? `${totals.cancellation_rate}%` : "\u2014"}</div>
                  <div className="muted" style={{ fontSize: 12 }}>{(totals.cancelled || 0)} {t("kpi_orders").toLowerCase()}</div>
                </div>
              </div>
              {branches.length > 1 ? (
                <div style={{ overflowX: "auto" }}>
                  <table className="data" style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#008080" }}>
                        <th style={{ color: "#fff", textAlign: "left", padding: "8px 10px" }}>{t("th_branch")}</th>
                        <th style={{ color: "#fff", textAlign: "right", padding: "8px 10px" }}>{t("cancel_rate")}</th>
                        <th style={{ color: "#fff", textAlign: "right", padding: "8px 10px" }}>{t("turnover_label")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {branches.map((b, i) => (
                        <tr key={b.id} style={{ background: i % 2 ? "#f2f9f8" : "#fff" }}>
                          <td style={{ padding: "6px 10px" }}><b>{b.name}</b></td>
                          <td style={{ padding: "6px 10px", textAlign: "right" }}>{b.cancellation_rate != null ? `${b.cancellation_rate}%` : "\u2014"}</td>
                          <td style={{ padding: "6px 10px", textAlign: "right" }}>{b.turnover != null ? b.turnover : "\u2014"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>{t("turnover_label")}: {t("turnover_hint")}</div>
                </div>
              ) : (
                <div className="muted" style={{ fontSize: 13 }}>{t("turnover_label")}: <b>{branches[0] && branches[0].turnover != null ? branches[0].turnover : "\u2014"}</b> ({t("turnover_hint")})</div>
              )}
            </section>
            {branches.length >= 2 && (
              <section className="card">
                <h3 style={{ marginTop: 0 }}>{"\ud83c\udfc6"} {t("lead_title")}</h3>
                <div style={{ overflowX: "auto" }}>
                  <table className="data" style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#008080" }}>
                        <th style={{ color: "#fff", textAlign: "left", padding: "8px 10px", width: 36 }}>#</th>
                        <th style={{ color: "#fff", textAlign: "left", padding: "8px 10px" }}>{t("th_branch")}</th>
                        <th style={{ color: "#fff", textAlign: "right", padding: "8px 10px" }}>{t("th_revenue")}</th>
                        <th style={{ color: "#fff", textAlign: "right", padding: "8px 10px" }}>{t("contribution")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {branches.map((b, i) => (
                        <tr key={b.id} style={{ background: i % 2 ? "#f2f9f8" : "#fff" }}>
                          <td style={{ padding: "6px 10px", color: "#8a97a6" }}>{i + 1}</td>
                          <td style={{ padding: "6px 10px" }}><b>{b.name}</b></td>
                          <td style={{ padding: "6px 10px", textAlign: "right" }}>{formatPrice(b.revenue, b.currency || cur)}</td>
                          <td style={{ padding: "6px 10px", textAlign: "right" }}>{b.contribution_pct != null ? `${b.contribution_pct}%` : "\u2014"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
            {branches.length >= 2 && (
              <div className="card" style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", padding:"10px 14px" }}>
                <b style={{ color:"#063b48" }}>{t("tab_branches")}</b>
                <span style={{ flex:1 }} />
                <button className={"btn small " + (!compareOn ? "" : "secondary")} onClick={() => setCompareOn(false)}>{t("per_branch")}</button>
                <button className={"btn small " + (compareOn ? "" : "secondary")} onClick={() => setCompareOn(true)}>{t("cmp_view")}</button>
              </div>
            )}
            {compareOn && branches.length >= 2 && (
              <section className="card">
                <h3 style={{ marginTop: 0 }}>{t("cmp_title")}</h3>
                <p className="muted" style={{ marginTop: -4, fontSize: 13 }}>{t("cmp_hint")}</p>
                {compareItems === undefined ? <p className="muted">{t("cmp_loading")}</p> :
                  (compareItems && compareItems.items && compareItems.items.length) ? (
                    <div style={{ overflowX: "auto" }}>
                      <table className="data" style={{ width: "100%", borderCollapse: "collapse", minWidth: 480 }}>
                        <thead>
                          <tr style={{ background: "#008080" }}>
                            <th rowSpan={2} style={{ textAlign: "left", padding: "8px 10px", color: "#fff", position:"sticky", left:0, background:"#008080" }}>{t("th_item")}</th>
                            {(compareItems.branches || []).map((b) => (
                              <th key={b.id} colSpan={2} style={{ padding: "6px 10px", color: "#fff", textAlign:"center", borderLeft:"1px solid rgba(255,255,255,.25)" }}>{b.name}</th>
                            ))}
                          </tr>
                          <tr style={{ background: "#0a8f8f" }}>
                            {(compareItems.branches || []).map((b) => (
                              <React.Fragment key={b.id}>
                                <th style={{ padding: "5px 8px", color: "#eafffb", textAlign:"right", fontWeight:500, fontSize:12, borderLeft:"1px solid rgba(255,255,255,.25)" }}>{t("th_sold")}</th>
                                <th style={{ padding: "5px 8px", color: "#eafffb", textAlign:"right", fontWeight:500, fontSize:12 }}>{t("th_rating")}</th>
                              </React.Fragment>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {compareItems.items.map((it, i) => (
                            <tr key={i} style={{ background: i % 2 ? "#f2f9f8" : "#fff" }}>
                              <td style={{ padding: "6px 10px", position:"sticky", left:0, background: i % 2 ? "#f2f9f8" : "#fff" }}>
                                <b>{it.name}</b>
                                {it.shared_by < (compareItems.branches || []).length && (
                                  <div className="muted" style={{ fontSize: 11 }}>{t("cmp_shared").replace("{n}", it.shared_by)}</div>
                                )}
                              </td>
                              {(compareItems.branches || []).map((b) => {
                                const cell = it.by_branch ? it.by_branch[String(b.id)] : null;
                                const rb = cell && cell.rating != null ? ratingStyle(cell.rating) : null;
                                return (
                                  <React.Fragment key={b.id}>
                                    <td style={{ padding: "6px 8px", textAlign:"right", fontWeight: cell && cell.sold ? 600 : 400, color: cell && cell.sold ? "#063b48" : "#c0c9d3", borderLeft:"1px solid #eef2f6" }}>{cell && cell.sold != null ? cell.sold : "\u2014"}</td>
                                    <td style={{ padding: "6px 8px", textAlign:"right" }}>
                                      {rb ? <span style={{ background: rb.bg, color: rb.fg, padding: "1px 7px", borderRadius: 999, fontSize: 11, fontWeight: 600 }}>{cell.rating} {"\u2605"}</span> : <span style={{ color:"#c0c9d3" }}>{"\u2014"}</span>}
                                    </td>
                                  </React.Fragment>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : <p className="muted">{t("cmp_none")}</p>}
              </section>
            )}
            {(!compareOn || branches.length < 2) && (
            <section className="card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <h3 style={{ margin: 0 }}>{t("branch_facts")}</h3>
                <select value={factsBranch} onChange={(e) => setFactsBranch(e.target.value)}>
                  {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              {factsB && factsB.logo_url && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                  <img src={resolveImageUrl(factsB.logo_url)} alt="" style={{ width: 30, height: 30, borderRadius: 7, objectFit: "cover" }} />
                  <b>{factsB.name}</b>
                </div>
              )}
              {factsB && (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px,1fr))", gap: 10, margin: "12px 0" }}>
                    <Mini label={t("waiters")} value={factsB.waiters != null ? factsB.waiters : "—"} tint="#E6F1FB" accent="#378ADD" />
                    <Mini label={t("chefs")} value={factsB.chefs != null ? factsB.chefs : "—"} tint="#E1F5EE" accent="#1D9E75" />
                    <Mini label={t("tables_qr")} value={factsB.tables != null ? factsB.tables : "—"} tint="#FAEEDA" accent="#BA7517" />
                    <Mini label={t("kpi_orders")} value={factsB.orders} tint="#EAF3DE" accent="#639922" />
                    <Mini label={t("kpi_revenue")} value={formatPrice(factsB.revenue, factsB.currency || cur)} tint="#EEEDFE" accent="#7F77DD" />
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "10px 0 6px" }}>
                    <h4 style={{ margin: 0 }}>{itemsMode === "top" ? t("top_items") : t("not_selling")}</h4>
                    <span style={{ flex: 1 }} />
                    <button className={"btn small " + (itemsMode === "top" ? "" : "secondary")} onClick={() => setItemsMode("top")}>{t("top_btn")}</button>
                    <button className={"btn small " + (itemsMode === "slow" ? "" : "secondary")} onClick={() => setItemsMode("slow")}>{t("not_selling_btn")}</button>
                  </div>
                  {topItems === undefined ? <p className="muted">{t("loading")}</p> :
                    (topItems && topItems.items && topItems.items.length) ? (
                      <div style={{ overflowX: "auto" }}>
                        <table className="data" style={{ width: "100%", borderCollapse: "collapse" }}>
                          <thead>
                            <tr style={{ background: "#008080" }}>
                              <th style={{ width: 32, padding: "8px 6px", color: "#fff" }}>#</th>
                              <th style={{ width: 52, color: "#fff" }}></th>
                              <th style={{ textAlign: "left", padding: "8px 10px", color: "#fff" }}>{t("th_item")}</th>
                              <th style={{ textAlign: "right", padding: "8px 10px", color: "#fff" }}>{t("th_sold")}</th>
                              <th style={{ textAlign: "right", padding: "8px 10px", color: "#fff" }}>{t("th_rating")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {topItems.items.map((it, i) => {
                              const rb = ratingStyle(it.avg_food_rating);
                              return (
                                <tr key={i} style={{ background: i % 2 ? "#f2f9f8" : "#fff" }}>
                                  <td style={{ textAlign: "center", color: "#8a97a6", fontSize: 13 }}>{i + 1}</td>
                                  <td style={{ padding: "6px 8px" }}>
                                    {it.image_url
                                      ? <img src={resolveImageUrl(it.image_url)} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: "cover" }} />
                                      : <div style={{ width: 40, height: 40, borderRadius: 8, background: "#eef2f6" }} />}
                                  </td>
                                  <td style={{ padding: "6px 10px" }}>
                                    <b>{it.name}</b>
                                    {it.category && <div className="muted" style={{ fontSize: 12 }}>{it.category}</div>}
                                  </td>
                                  <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 600 }}>{it.quantity}<span className="muted" style={{ fontWeight: 400, fontSize: 12 }}> {t("sold")}</span></td>
                                  <td style={{ padding: "6px 10px", textAlign: "right" }}>
                                    {it.avg_food_rating == null
                                      ? <span className="muted">—</span>
                                      : <span style={{ background: rb.bg, color: rb.fg, padding: "2px 8px", borderRadius: 999, fontSize: 12, fontWeight: 600 }}>{it.avg_food_rating} ★</span>}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : <p className="muted">{itemsMode === "top" ? t("no_items_sold") : t("all_items_sales")}</p>}
                </>
              )}
            </section>
            )}

            <section className="card">
              <h3 style={{ marginTop: 0 }}>{t("branch_detail")}</h3>
              <table className="data">
                <thead>
                  <tr>
                    <th>{t("th_branch")}</th><th>{t("th_plan")}</th>
                    <th style={{ textAlign: "right" }}>{t("waiters")}</th>
                    <th style={{ textAlign: "right" }}>{t("chefs")}</th>
                    <th style={{ textAlign: "right" }}>{t("tables_qr")}</th>
                    <th style={{ textAlign: "right" }}>{t("kpi_orders")}</th>
                    <th style={{ textAlign: "right" }}>{t("kpi_revenue")}</th>
                    <th style={{ textAlign: "right" }}>{t("th_food_cost")}</th>
                    <th style={{ textAlign: "right" }}>{t("th_purchasing")}</th>
                    <th style={{ textAlign: "right" }}>{t("th_shrinkage")}</th>
                    <th style={{ textAlign: "right" }}>{t("th_cxl")}</th>
                    <th style={{ textAlign: "right" }}>{t("th_events")}</th>
                    <th style={{ textAlign: "right" }}>{t("food")}</th>
                    <th style={{ textAlign: "right" }}>{t("service")}</th>
                  </tr>
                </thead>
                <tbody>
                  {branches.map((b) => (
                    <tr key={b.id}>
                      <td>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                          {b.logo_url ? (
                            <img src={resolveImageUrl(b.logo_url)} alt="" style={{ width: 22, height: 22, borderRadius: 5, objectFit: "cover" }} />
                          ) : (
                            <span style={{ width: 22, height: 22, borderRadius: 5, background: "#eef2f6", display: "inline-block" }} />
                          )}
                          <b>{b.name}</b>
                        </span>
                      </td>
                      <td className="muted">{b.subscription_tier}</td>
                      <td style={{ textAlign: "right" }}>{b.waiters != null ? b.waiters : "—"}</td>
                      <td style={{ textAlign: "right" }}>{b.chefs != null ? b.chefs : "—"}</td>
                      <td style={{ textAlign: "right" }}>{b.tables != null ? b.tables : "—"}</td>
                      <td style={{ textAlign: "right" }}>{b.orders}</td>
                      <td style={{ textAlign: "right" }}>{formatPrice(b.revenue, b.currency || cur)}</td>
                      <td style={{ textAlign: "right", color: b.food_cost_pct != null && b.food_cost_pct > 35 ? "#c0392b" : undefined, fontWeight: b.food_cost_pct != null && b.food_cost_pct > 35 ? 700 : undefined }}>{b.food_cost_pct == null ? "—" : `${b.food_cost_pct}%`}</td>
                      <td style={{ textAlign: "right" }}>{b.purchasing_spend ? formatPrice(b.purchasing_spend, b.currency || cur) : "—"}</td>
                      <td style={{ textAlign: "right", color: b.shrinkage_value ? "#c0392b" : undefined, fontWeight: b.shrinkage_value ? 700 : undefined }}>{b.shrinkage_value ? formatPrice(b.shrinkage_value, b.currency || cur) : "—"}</td>
                      <td style={{ textAlign: "right", color: b.cancellation_rate != null && b.cancellation_rate > 5 ? "#c0392b" : undefined, fontWeight: b.cancellation_rate != null && b.cancellation_rate > 5 ? 700 : undefined }}>{b.cancellation_rate == null ? "—" : `${b.cancellation_rate}%`}</td>
                      <td style={{ textAlign: "right" }}>{b.event_revenue ? formatPrice(b.event_revenue, b.currency || cur) : "—"}</td>
                      <td style={{ textAlign: "right" }}>{b.avg_food_rating == null ? "—" : `${b.avg_food_rating} ★`}</td>
                      <td style={{ textAlign: "right" }}>{b.avg_service_rating == null ? "—" : `${b.avg_service_rating} ★`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {branches.length === 0 && <p className="muted">{t("no_branches")}</p>}
            </section>
            </>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}
